// Dashboard.js
import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactEditList from "react-edit-list";
import { getDocs, collection, query, addDoc, orderBy, doc, deleteDoc, setDoc } from 'firebase/firestore/lite';
import {db} from './../firebase';
import { DASHBOARD_CRUD, notifyDashboardCrudError, failDashboardValidation, isBlank } from '../utils/dashboardCrud';
import FileUploader from './../FileUploader';

// Using native HTML5 drag and drop
import { useDragAndDrop } from './useDragAndDrop';

// Iconos disponibles (valores ASCII para compatibilidad con build/CI)
const iconOptions = [
  { value: "AlmacenamientoEnergiaW.png", name: "Almacenamiento de Energía" },
  { value: "ConversionEnergiaW.png", name: "Conversión de Energía" },
  { value: "NanomaterialesW.png", name: "Nanomateriales" },
  { value: "path11826.png", name: "Biosensores Electroquímicos" },
  { value: "MusculosArtificialesW.png", name: "Músculos Artificiales" },
  { value: "BioElectronica.png", name: "Bioelectrónica" },
  { value: "software.png", name: "Software" },
  { value: "mecatronicaR.png", name: "Mecatrónica" },
  { value: "logotejidosR1.png", name: "Ingeniería de Tejidos" }
];

// Mapa estático para evitar require() dinámico con caracteres especiales (falla en CI)
const ICON_REQUIRE = {
  "AlmacenamientoEnergiaW.png": require('../assets/icons-lineas/AlmacenamientoEnergiaW.png'),
  "ConversionEnergiaW.png": require('../assets/icons-lineas/ConversionEnergiaW.png'),
  "NanomaterialesW.png": require('../assets/icons-lineas/NanomaterialesW.png'),
  "path11826.png": require('../assets/icons-lineas/path11826.png'),
  "MusculosArtificialesW.png": require('../assets/icons-lineas/MusculosArtificialesW.png'),
  "BioElectronica.png": require('../assets/icons-lineas/BioElectronica.png'),
  "software.png": require('../assets/icons-lineas/software.png'),
  "mecatronicaR.png": require('../assets/icons-lineas/mecatronicaR.png'),
  "logotejidosR1.png": require('../assets/icons-lineas/logotejidosR1.png')
};
const getIconPath = (iconValue) => {
  if (!iconValue) return null;
  const key = iconValue === "BioElectrónica.png" ? "BioElectronica.png" : iconValue;
  return ICON_REQUIRE[key] || null;
};

// Colores disponibles
const colorOptions = [
  { value: "color-1", name: "Color 1 (Azul oscuro)" },
  { value: "color-2", name: "Color 2 (Gris)" },
  { value: "color-3", name: "Color 3 (Verde)" }
];

// This is the schema
const schema = [
  { name: "id", type: "id" },
  { name: "name", type: "string" },
  { name: "icon", type: iconOptions },
  { name: "color", type: colorOptions },
  { name: "image", type: "custom" },
  { name: "content", type: "custom" },
  { name: "enabled", type: [
      { value: true, name: "Si" },
      { value: false, name: "No" }
    ] 
  }
];

const headers = {
  "name"      : "Nombre",
  "icon"      : "", // Oculto con CSS
  "color"     : "Color",
  "image"     : "Imagen principal",
  "content"   : "Contenido",
  "enabled"   : "Mostrar"
};

export const ResearchLinesList = () => {

  const DOCUMENT_NAME = 'research-lines';

  const TITLE = 'Líneas de Investigación';
  
  const [data, setData] = useState([]);
  const dataRef = useRef([]);

  const navigate = useNavigate();

  const goToDashboard = async () => {
    navigate('/dashboard');
  };

  // Keep dataRef in sync with data state
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const ref = useRef(null);
  
  // Use drag and drop hook
  const { initDragAndDrop, isSaving } = useDragAndDrop(data, setData, dataRef, DOCUMENT_NAME, TITLE, ref);

  const fetchData = async () => {
    try {
      const q = query(collection(db, DOCUMENT_NAME), orderBy('order'));
      const querySnapshot = await getDocs(q);
      const newData = querySnapshot.docs
        .map((d) => ({ ...d.data(), id: d.id }));
      setData(newData);
      dataRef.current = newData;
      
      // Initialize drag and drop after data is loaded
      setTimeout(() => {
        initDragAndDrop();
      }, 1000);
    } catch (error) {
      notifyDashboardCrudError({ operation: DASHBOARD_CRUD.LOAD, entityName: TITLE, error });
      setData([]);
    }
  };

  useEffect(()=>{
    fetchData();
    // Inyectar estilos para ocultar la columna de icono
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .table th:nth-child(2),
      .table td:nth-child(2) {
        display: none !important;
      }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      // Limpiar el estilo al desmontar
      document.head.removeChild(styleElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="container-fluid mt-1">
        <div className="card shadow">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <button className="btn btn-danger" onClick={goToDashboard}>Regresar</button>
                <h3 className="mb-0">Listado de {TITLE}</h3>
                {isSaving && (
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-light me-2" role="status">
                      <span className="visually-hidden">Guardando...</span>
                    </div>
                    <span className="text-light">Guardando cambios...</span>
                  </div>
                )}
            </div>
            <div className="card-body">
                <div className="table-responsive">
                    <React.Fragment>
                      <div className="m-3 fw-bold">
                        Se pueden reordenar las {TITLE.toLowerCase()} moviéndolas de orden en la lista
                      </div>
                      <ReactEditList
                        schema={schema}
                        headers={headers}
                        onLoad={() => data}
                        ref={ref}
                        format={{
                          image: function MainImageRenderer(props) {
                            const currentItem = props.item || {};
                            if (props.value && (props.value.startsWith('http://') || props.value.startsWith('https://'))) {
                              return (
                                <div title="Imagen principal">
                                  <img
                                    src={props.value}
                                    alt="Imagen principal de la línea"
                                    style={{ maxWidth: '80px', maxHeight: '60px', objectFit: 'contain', display: 'block' }}
                                  />
                                </div>
                              );
                            }
                            const iconValue = currentItem.icon;
                            if (iconValue) {
                              const iconPath = getIconPath(iconValue);
                              if (iconPath) {
                                return (
                                  <div title="Sin imagen principal (usa icono)">
                                    <img
                                      src={iconPath}
                                      alt="Icono"
                                      style={{ maxWidth: '60px', height: 'auto', display: 'block', opacity: 0.8 }}
                                    />
                                    <small className="text-muted d-block" style={{ fontSize: '10px' }}>icono</small>
                                  </div>
                                );
                              }
                              return <span style={{ fontSize: '12px' }}>{iconValue}</span>;
                            }
                            return <span className="text-muted">Sin imagen</span>;
                          },
                          content: function ContentRenderer(props) {
                            if (!props.value || !Array.isArray(props.value) || props.value.length === 0) {
                              return <span style={{ color: '#999', fontStyle: 'italic' }}>Sin contenido</span>;
                            }
                            const MAX_ITEMS = 2;
                            const displayedItems = props.value.slice(0, MAX_ITEMS);
                            const hasMore = props.value.length > MAX_ITEMS;
                            
                            return (
                              <div style={{ maxWidth: '300px' }}>
                                {displayedItems.map((block, idx) => {
                                  if (block.type === 'title') {
                                    return <div key={idx} className="fw-bold mb-1">{block.text || block.content}</div>;
                                  } else if (block.type === 'paragraph') {
                                    const text = block.text || block.content || '';
                                    const truncated = text.length > 50 ? text.substring(0, 50) + '...' : text;
                                    return <div key={idx} className="mb-1">{truncated}</div>;
                                  } else if (block.type === 'image') {
                                    return <div key={idx} className="mb-1">Imagen</div>;
                                  }
                                  return null;
                                })}
                                {hasMore && (
                                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                                    ... y {props.value.length - MAX_ITEMS} bloques más
                                  </div>
                                )}
                              </div>
                            );
                          }
                        }}
                        edit={{
                          image: function MainImageEditor(props) {
                            const currentItem = props.item || {};
                            const hasMainImage = props.value && (props.value.startsWith('http://') || props.value.startsWith('https://'));
                            const iconValue = currentItem.icon;
                            return (
                              <div style={{ minWidth: '200px' }}>
                                <small className="text-muted d-block mb-2 fw-bold">Imagen principal de la línea</small>
                                <p className="small text-muted mb-2">
                                  Se muestra en la página de la línea. Si no se sube, se usará el icono.
                                </p>
                                {hasMainImage && (
                                  <div className="mb-2">
                                    <img
                                      src={props.value}
                                      alt="Imagen principal actual"
                                      style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain', display: 'block', marginBottom: '6px' }}
                                    />
                                    <div className="d-flex gap-1 flex-wrap">
                                      <FileUploader
                                        url={props.value}
                                        onUpload={(file) => props.onChange(file)}
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => props.onChange(null)}
                                        title="Quitar imagen principal (usar solo icono)"
                                      >
                                        Quitar imagen
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {!hasMainImage && (
                                  <div>
                                    {iconValue && getIconPath(iconValue) && (
                                      <div className="mb-2">
                                        <small className="text-muted d-block mb-1">Fallback actual (icono):</small>
                                        <img
                                          src={getIconPath(iconValue)}
                                          alt="Icono"
                                          style={{ maxWidth: '40px', height: 'auto', display: 'block' }}
                                        />
                                      </div>
                                    )}
                                    <FileUploader
                                      url={null}
                                      onUpload={(file) => props.onChange(file)}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          },
                          content: function ContentEditor(props) {
                            const [contentBlocks, setContentBlocks] = React.useState(props.value || []);
                            
                            React.useEffect(() => {
                              setContentBlocks(props.value || []);
                            }, [props.value]);
                            
                            const addBlock = (type) => {
                              const newBlock = { 
                                type, 
                                id: Date.now(),
                                text: type === 'title' ? '' : '',
                                content: type === 'image' ? null : '',
                                imageUrl: type === 'image' ? null : null
                              };
                              const updated = [...contentBlocks, newBlock];
                              setContentBlocks(updated);
                              props.onChange(updated);
                            };
                            
                            const updateBlock = (index, field, value) => {
                              const updated = [...contentBlocks];
                              updated[index] = { ...updated[index], [field]: value };
                              setContentBlocks(updated);
                              props.onChange(updated);
                            };
                            
                            const deleteBlock = (index) => {
                              if (!window.confirm("¿Eliminar este bloque?")) return;
                              const updated = contentBlocks.filter((_, i) => i !== index);
                              setContentBlocks(updated);
                              props.onChange(updated);
                            };
                            
                            const moveBlock = (index, direction) => {
                              if ((direction === 'up' && index === 0) || (direction === 'down' && index === contentBlocks.length - 1)) {
                                return;
                              }
                              const updated = [...contentBlocks];
                              const newIndex = direction === 'up' ? index - 1 : index + 1;
                              [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
                              setContentBlocks(updated);
                              props.onChange(updated);
                            };
                            
                            return (
                              <div style={{ minWidth: '600px', maxWidth: '800px' }}>
                                <div className="mb-2 d-flex gap-2 flex-wrap">
                                  <button 
                                    type="button" 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => addBlock('title')}
                                  >
                                    + Título
                                  </button>
                                  <button 
                                    type="button" 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => addBlock('paragraph')}
                                  >
                                    + Párrafo
                                  </button>
                                  <button 
                                    type="button" 
                                    className="btn btn-sm btn-outline-info"
                                    onClick={() => addBlock('image')}
                                  >
                                    + Imagen
                                  </button>
                                </div>
                                
                                <div className="border rounded p-2" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                  {contentBlocks.length === 0 ? (
                                    <div className="text-muted text-center py-3">
                                      No hay bloques de contenido. Agrega uno usando los botones arriba.
                                    </div>
                                  ) : (
                                    contentBlocks.map((block, index) => (
                                      <div key={block.id || index} className="border rounded p-2 mb-2 bg-light">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                          <span className="badge bg-secondary">{block.type === 'title' ? 'Título' : block.type === 'paragraph' ? 'Párrafo' : 'Imagen'}</span>
                                          <div className="btn-group btn-group-sm">
                                            <button 
                                              type="button" 
                                              className="btn btn-outline-secondary"
                                              onClick={() => moveBlock(index, 'up')}
                                              disabled={index === 0}
                                              title="Mover arriba"
                                            >
                                              Arriba
                                            </button>
                                            <button 
                                              type="button" 
                                              className="btn btn-outline-secondary"
                                              onClick={() => moveBlock(index, 'down')}
                                              disabled={index === contentBlocks.length - 1}
                                              title="Mover abajo"
                                            >
                                              Abajo
                                            </button>
                                            <button 
                                              type="button" 
                                              className="btn btn-outline-danger"
                                              onClick={() => deleteBlock(index)}
                                              title="Eliminar"
                                            >
                                              -
                                            </button>
                                          </div>
                                        </div>
                                        
                                        {block.type === 'title' && (
                                          <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Título..."
                                            value={block.text || block.content || ''}
                                            onChange={(e) => {
                                              updateBlock(index, 'text', e.target.value);
                                              updateBlock(index, 'content', e.target.value);
                                            }}
                                          />
                                        )}
                                        
                                        {block.type === 'paragraph' && (
                                          <textarea
                                            className="form-control"
                                            rows="3"
                                            placeholder="Texto del párrafo..."
                                            value={block.text || block.content || ''}
                                            onChange={(e) => {
                                              updateBlock(index, 'text', e.target.value);
                                              updateBlock(index, 'content', e.target.value);
                                            }}
                                          />
                                        )}
                                        
                                        {block.type === 'image' && (
                                          <div>
                                            {block.imageUrl ? (
                                              <div>
                                                <img 
                                                  src={block.imageUrl} 
                                                  alt="Preview" 
                                                  style={{ maxWidth: '200px', maxHeight: '150px', marginBottom: '10px' }}
                                                />
                                                <FileUploader
                                                  url={block.imageUrl}
                                                  onUpload={(url) => {
                                                    updateBlock(index, 'imageUrl', url);
                                                    updateBlock(index, 'content', url);
                                                  }}
                                                />
                                              </div>
                                            ) : (
                                              <FileUploader
                                                url={null}
                                                onUpload={(url) => {
                                                  updateBlock(index, 'imageUrl', url);
                                                  updateBlock(index, 'content', url);
                                                }}
                                              />
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          }
                        }}
                        onUpdate={async (item) => {
                          // Call your API here
                          if (isBlank(item.name)) {
                            return failDashboardValidation({
                              operation: DASHBOARD_CRUD.UPDATE,
                              entityName: TITLE,
                              missingFields: ['Nombre'],
                              message: 'Debes capturar el nombre de la línea de investigación.',
                            });
                          }
                          if (!item.image) {
                            item.image = null;
                          }
                          if (!item.content) {
                            item.content = [];
                          }
                          if (!item.icon) {
                            item.icon = iconOptions[0].value;
                          }
                          if (!item.color) {
                            item.color = colorOptions[0].value;
                          }
                          item.enabled = item.enabled === 'false' || item.enabled === false ? false : true;
                          try {
                            await setDoc(doc(db, DOCUMENT_NAME, item.id), item);
                          } catch (error) {
                            notifyDashboardCrudError({ operation: DASHBOARD_CRUD.UPDATE, entityName: TITLE, error });
                            return false;
                          }
                        }}
                        onDelete={async (item) => {
                          if (!window.confirm("¿Estás seguro de que quieres eliminar esta línea de investigación?")) return false;
                          // Call your API here
                          try {
                            await deleteDoc(doc(db, DOCUMENT_NAME, item.id));
                          } catch (error) {
                            notifyDashboardCrudError({ operation: DASHBOARD_CRUD.DELETE, entityName: TITLE, error });
                            return false;
                          }
                        }}
                        onInsert={async (item) => {
                          // Call your API here
                          if (isBlank(item.name)) {
                            return failDashboardValidation({
                              operation: DASHBOARD_CRUD.CREATE,
                              entityName: TITLE,
                              missingFields: ['Nombre'],
                              message: 'Debes capturar el nombre de la línea de investigación.',
                            });
                          }
                          if (!item.image) {
                            item.image = null;
                          }
                          if (!item.icon) {
                            item.icon = iconOptions[0].value;
                          }
                          if (!item.color) {
                            item.color = colorOptions[0].value;
                          }
                          item.enabled = item.enabled === 'false' || item.enabled === false ? false : true;
                          item.order = data.length;
                          item.createdDate = new Date();
                          item.updatedDate = new Date();
                          try {
                            const docRef = await addDoc(collection(db, DOCUMENT_NAME), item);
                            item.id = docRef.id;
                          } catch (error) {
                            notifyDashboardCrudError({ operation: DASHBOARD_CRUD.CREATE, entityName: TITLE, error });
                            return false;
                          }
                        }}
                        className="table table-light table-fixed align-middle"
                        headClassName="table-primary"
                        inputClassName="w-100"
                        thClassName={{
                          name: "col-3",
                          icon: "d-none", // Ocultar columna de icono
                          color: "col-2",
                          image: "col-2",
                          content: "col-8",
                          enabled: "col-2"
                        }}
                        bodyClassName="sortable-items"
                        btnValidateClassName="btn btn-success p-0 m-0"
                        btnDeleteClassName="btn btn-danger py-0 px-1 m-0 mx-1"
                        btnCancelClassName="btn btn-secondary py-0 px-1 m-0 mx-1"
                        rowClassName="draggable"
                        insertClassName="not-draggable"
                        filler={<React.Fragment>&#8230;</React.Fragment>}
                      />
                    </React.Fragment>
                </div>
            </div>
        </div>
    </div>
  );
}

